import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSEO } from "../lib/useSEO";
import { FAQS, OHIO_CITIES, SERVICES } from "../data/content";
import { ACCIDENTS } from "../data/accidents";

export default function FAQ() {
  useSEO(
    "FAQ · Pre-settlement funding questions answered",
    "Common questions about pre-settlement legal funding: how it works, what it costs, who qualifies, and how fast you get paid. Plain-English answers.",
    { canonical: "https://instabridgefunding.com/faq" }
  );

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navbar />

      <section className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">FAQ</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            Your questions, <span className="font-semibold">answered.</span>
          </h1>
          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed max-w-2xl">
            Plain-English answers about pre-settlement funding. If you don't find what you need, contact us — we usually reply within an hour.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Accordion type="single" collapsible data-testid="faq-page-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-b border-[#E5E7EB]">
                <AccordionTrigger className="font-display text-lg text-[#0A2540] py-5 hover:no-underline text-left" data-testid={`faq-page-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#6B7280] leading-relaxed pb-6 text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16 bg-[#F9FAFB] border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="uppercase-label mb-3">By case type</div>
            <h3 className="font-display text-lg font-semibold text-[#0A2540]">Accident-type guides</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {ACCIDENTS.map((a) => (
                <li key={a.slug}>
                  <Link to={`/accidents/${a.slug}`} className="text-[#0066FF] hover:underline" data-testid={`faq-accident-link-${a.slug}`}>
                    {a.label} funding →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="uppercase-label mb-3">By Ohio city</div>
            <h3 className="font-display text-lg font-semibold text-[#0A2540]">Local funding pages</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {OHIO_CITIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/states/ohio/${c.slug}`} className="text-[#0066FF] hover:underline" data-testid={`faq-city-link-${c.slug}`}>
                    {c.name} pre-settlement funding →
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/states/ohio" className="text-[#0066FF] hover:underline font-medium">All Ohio pages →</Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="uppercase-label mb-3">By service</div>
            <h3 className="font-display text-lg font-semibold text-[#0A2540]">Services & tools</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-[#0066FF] hover:underline">
                    {s.name} →
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/tools/funding-cost-calculator" className="text-[#0066FF] hover:underline">Funding cost calculator →</Link>
              </li>
              <li>
                <Link to="/tools/settlement-distribution-calculator" className="text-[#0066FF] hover:underline">Settlement distribution calculator →</Link>
              </li>
              <li>
                <Link to="/how-it-works" className="text-[#0066FF] hover:underline">How it works →</Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl font-semibold">Still have questions?</h2>
          <p className="mt-3 text-white/70">Our team usually replies within an hour during business hours.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/apply"><Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 px-6">Apply now</Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A2540] rounded-full h-11 px-6">Contact us</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
